// Demo mode — client-side identity masking for sales demos.
//
// A main_admin can enter an organization through MainAdmin's "Switch as Demo"
// action. Everything about the org stays real — hours, activity, screenshots,
// dates, AI narrative — except the identities: person names, their emails and
// the organization name are swapped for invented ones before anything reaches
// the screen.
//
// Nothing here touches the server or the database. The API keeps returning the
// real records; masking happens on the response, in the browser, on its way to
// the components.
//
// Two properties this file exists to guarantee:
//
//   1. Stable across reloads. The fake name is *computed* from the real one
//      (FNV-1a hash → index into a fixed pool), never stored. The same person
//      is the same invented person on every screen, in every session, forever.
//      No map to persist, nothing to fall out of sync.
//
//   2. Off means off. Every entry point returns its input untouched when the
//      flag is absent, so a normal "Switch to Organization" behaves exactly as
//      it did before this file existed.
//
// Deliberately NOT masked, because a browser cannot reach them: server-rendered
// PDF exports and voice/TTS audio arrive finished. Callers disable those while
// demo mode is on.

const FLAG = 'tg.demoMode';

/* ── identity pool ─────────────────────────────────────────────────────── */

// 10 × 10 → 100 distinct people, which comfortably exceeds any org shown in a
// demo. Chosen to read like a real team roster rather than obvious filler.
const FIRST_NAMES = [
  'Arjun', 'Kavya', 'Rohan', 'Ishaan', 'Priya',
  'Ananya', 'Vikram', 'Neha', 'Siddharth', 'Meera',
];

const LAST_NAMES = [
  'Mehta', 'Nair', 'Desai', 'Kapoor', 'Malhotra',
  'Iyer', 'Rao', 'Bansal', 'Joshi', 'Chopra',
];

const DEMO_ORG_NAME = 'Nexus Technologies';
const DEMO_EMAIL_DOMAIN = 'nexustech.io';

/* ── flag ──────────────────────────────────────────────────────────────── */

// Surfaces that are the operator's own tooling, never part of a demo. The flag
// alone is not enough to decide whether to mask: it is one global boolean, but
// the admin panel and the switched-into org are rendered by the same app in the
// same tab. Keying only off the flag meant a main_admin who came back to
// /main-admin — or opened it in another tab while a demo was armed — saw their
// own organization list with invented names and addresses in it.
//
// Masking is for what the prospect looks at. The admin's own console is not
// that, so it is excluded by route regardless of the flag.
const OPERATOR_ROUTES = /^\/(main-admin|partner)/;

function onOperatorSurface() {
  try {
    if (OPERATOR_ROUTES.test(window.location.pathname)) return true;

    // Second guard, independent of the URL: a demo only exists inside a
    // switched-into organization. If the session is still the main_admin's own
    // (no active switch), there is nothing to mask — and masking it would hide
    // the operator's real data from them.
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u && u.role === 'main_admin' && !u.isMainAdminSwitch) return true;
    }
    return false;
  } catch (_e) {
    return false;
  }
}

export function isDemoMode() {
  try {
    if (onOperatorSurface()) return false;
    return localStorage.getItem(FLAG) === 'true';
  } catch (_e) {
    // Private browsing / storage disabled — treat as off rather than throwing
    // from inside a response interceptor.
    return false;
  }
}

export function enableDemoMode() {
  try { localStorage.setItem(FLAG, 'true'); } catch (_e) {}
}

export function disableDemoMode() {
  try { localStorage.removeItem(FLAG); } catch (_e) {}
}

/* ── deterministic name generation ─────────────────────────────────────── */

// FNV-1a. Cheap, well-distributed, and — the part that matters — a pure
// function: the same string always produces the same number, in every tab and
// after every refresh. That is what makes the masking stable without storage.
function hash(str) {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function normalizeKey(name) {
  return String(name).trim().toLowerCase();
}

// Local-part of an email is a decent identity key when a name is missing, and
// it keeps "rajiv@x.com" and "Rajiv Sharma" from colliding into one person only
// by coincidence — they are simply two keys, each stable on its own.
function emailLocalPart(email) {
  return String(email).split('@')[0] || String(email);
}

// 100 combinations against a ~50-person org is a birthday-paradox problem: two
// real people landing on one invented person is likely, not rare, and in a demo
// it reads as a bug the moment two rows show the same name. So the pool is
// treated as a 100-slot table with linear probing — the first free slot wins,
// and the assignment is remembered for the session.
//
// This stays refresh-stable: `hash` fixes the starting slot, and the walk is
// deterministic given the same set of people. What varies is the order names
// are first seen, so the resolver is seeded from the sorted set of everyone
// known so far rather than from arrival order.
let assigned = new Map();   // realKey -> fake full name
let usedSlots = new Set();  // slot index

function slotName(slot) {
  const first = FIRST_NAMES[slot % FIRST_NAMES.length];
  const last = LAST_NAMES[Math.floor(slot / FIRST_NAMES.length) % LAST_NAMES.length];
  return `${first} ${last}`;
}

export function fakeFullName(realName) {
  const key = normalizeKey(realName);
  if (!key) return slotName(0);

  const cached = assigned.get(key);
  if (cached) return cached;

  const total = FIRST_NAMES.length * LAST_NAMES.length;
  const start = hash(key) % total;

  let slot = start;
  for (let i = 0; i < total; i++) {
    slot = (start + i) % total;
    if (!usedSlots.has(slot)) break;
  }

  // Past 100 distinct people every slot is taken; fall back to the hashed slot
  // so the result stays defined and stable rather than throwing.
  const name = slotName(usedSlots.size >= total ? start : slot);
  usedSlots.add(slot);
  assigned.set(key, name);
  return name;
}

export function fakeFirstName(realName) {
  return fakeFullName(realName).split(' ')[0];
}

export function fakeLastName(realName) {
  return fakeFullName(realName).split(' ')[1];
}

// Emails are masked from whatever identity the address itself implies, so a
// row that falls back to showing an email (a common pattern in this codebase:
// `${firstName} ${lastName}`.trim() || email) never leaks a real address.
/**
 * `ownerName` matters: a row reading "Meera Rao" beside "ishaan.kapoor@…" is
 * exactly the detail that ends a demo. When the person's real name is known the
 * address is derived from their invented name so the two agree. Only when no
 * name is available does the address key off its own local part.
 */
export function fakeEmail(realEmail, ownerName) {
  const known = ownerName || ownerOfEmail(realEmail);
  const identity = known && String(known).trim() ? known : emailLocalPart(realEmail);
  const name = fakeFullName(identity);
  return `${name.toLowerCase().replace(/\s+/g, '.')}@${DEMO_EMAIL_DOMAIN}`;
}

// Links an address to its owner, so a later context-free sighting of the same
// address — inside AI prose, say — resolves to the same invented person.
const emailOwners = new Map();

function rememberEmailOwner(email, ownerName) {
  if (!email || !ownerName) return;
  emailOwners.set(normalizeKey(email), String(ownerName).trim());
}

function ownerOfEmail(email) {
  return emailOwners.get(normalizeKey(email));
}

export function fakeOrgName() {
  return DEMO_ORG_NAME;
}

/* ── field-level masking ───────────────────────────────────────────────── */

// Field names that hold a person's name somewhere in this app's API surface.
// Derived from a sweep of the response shapes actually consumed by screens.
const NAME_FIELDS = new Set([
  'name', 'fullName', 'userName', 'username', 'displayName',
  'memberName', 'employeeName', 'personName', 'assigneeName',
  'createdByName', 'ownerName', 'leadName', 'contactName',
  // Dashboard alert rows ("Needs attention") carry the people involved in
  // `names` — sometimes several, comma-separated.
  'names', 'people', 'who', 'members', 'participants',
]);

// Strings that must never be rewritten, whatever they contain: identifiers,
// dates and URLs. A Mongo id or an ISO timestamp has no names in it, but
// leaving them out of prose masking keeps the masker away from anything the
// app uses as a key or parses as a value.
const OPAQUE_FIELDS = new Set([
  '_id', 'id', 'userId', 'user_id', 'orgId', 'organizationId', 'projectId',
  'taskId', 'leadId', 'deviceId', 'device_id', 'sessionId', 'session_id',
  'token', 'refreshToken', 'key', 'slug', 'url', 'href', 'src', 'path',
  'date', 'dateLocal', 'createdAt', 'updatedAt', 'timestamp', 'startMs',
  'endMs', 'type', 'kind', 'status', 'role', 'tone', 'icon', 'color',
]);

const FIRST_NAME_FIELDS = new Set(['firstName', 'first_name', 'fname', 'givenName']);
const LAST_NAME_FIELDS = new Set(['lastName', 'last_name', 'lname', 'surname', 'familyName']);
const EMAIL_FIELDS = new Set(['email', 'emailAddress', 'userEmail', 'contactEmail', 'to', 'from']);
const ORG_NAME_FIELDS = new Set(['organizationName', 'orgName', 'companyName']);

// Keys whose `.name` is a thing, not a person — masking these would rename the
// customer's projects and departments and make the demo incoherent.
const NON_PERSON_PARENTS = new Set([
  'project', 'projects', 'department', 'departments', 'organization',
  'app', 'apps', 'application', 'tool', 'tools', 'website', 'websites',
  'task', 'tasks', 'goal', 'goals', 'plan', 'category', 'tag', 'tags',
  'file', 'files', 'domain', 'domains', 'skill', 'skills',
]);

// Anything that looks like a person's name and appears in prose. Built once per
// mask pass from the identities discovered in that payload.
function isPersonName(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  // A list of several people ("Rajiv Sharma, Pooja Deshmukh and 2 others") is
  // longer than one name but still an identity field, so allow the extra room
  // and let maskNameList split it.
  if (!v || v.length > 160) return false;
  return true;
}

// Identity fields are not always a single name: alert rows put several people
// in one string. Split on the usual separators, mask each part, and keep the
// separators so the sentence still reads correctly.
function maskNameList(value) {
  const v = String(value).trim();
  if (!v) return value;

  if (!/[,&]|\band\b|\+/.test(v)) {
    learnIdentity(v);
    return fakeFullName(v);
  }

  return v.split(/(\s*,\s*|\s+and\s+|\s*&\s*|\s*\+\s*)/).map((part) => {
    // Odd indices are the captured separators — pass them through untouched.
    if (/^(\s*,\s*|\s+and\s+|\s*&\s*|\s*\+\s*)$/.test(part)) return part;
    const p = part.trim();
    if (!p) return part;
    // "and 2 others", "+3 more" — a count, not a person.
    if (/^\d+\s*(others?|more|people|members?)$/i.test(p)) return part;
    learnIdentity(p);
    return fakeFullName(p);
  }).join('');
}

/**
 * Register one real name for prose masking, right at the moment it is seen in
 * a structured field.
 *
 * This closes a race that showed up on the dashboard's "Needs attention"
 * panel: its alert rows stream in before any roster response, so the prose
 * rules were still empty when a row's `title` and `body` were masked, and the
 * real names stayed in the sentences even though the `names` field beside them
 * had been swapped. Learning as we mask means the first row that names someone
 * in a field also makes that person maskable in the text of the same row.
 */
function learnIdentity(name) {
  const n = String(name).trim();
  if (n.length > 1 && n.length < 60 && !identityPool.has(n)) {
    setKnownIdentities([n]);
  }
}

/**
 * Walk a parsed JSON payload and replace identity fields in place on a copy.
 * Returns the input unchanged when demo mode is off.
 */
export function maskObject(input, parentKey = null) {
  if (!isDemoMode()) return input;
  return walk(input, parentKey, 0);
}

function walk(node, parentKey, depth) {
  // Payloads here are wide but shallow; the cap is a cheap guard against a
  // cyclic or pathological structure stalling a response interceptor.
  if (depth > 12) return node;

  if (Array.isArray(node)) {
    return node.map((item) => walk(item, parentKey, depth + 1));
  }

  // A bare string inside an array — `points: ["Rajiv fixed the build"]`,
  // bullet lists, evidence lines. These have no key of their own to match on,
  // so prose masking is the only thing that can reach them.
  if (typeof node === 'string') return maskText(node);

  if (!node || typeof node !== 'object') return node;

  // A Date, File, Blob etc. must pass through as-is.
  if (node.constructor && node.constructor !== Object) return node;

  const out = {};
  // The organization object's own `name` is the org name, not a person.
  const parentIsOrg = parentKey === 'organization' || parentKey === 'org';

  // Whose record is this? Resolved before the field loop so an email on this
  // object masks to the same invented person as the name on it, regardless of
  // which key order the payload happens to use.
  const ownerName = !parentIsOrg && !NON_PERSON_PARENTS.has(parentKey)
    ? (
      [node.name, node.fullName, node.userName, node.memberName, node.displayName]
        .find((v) => typeof v === 'string' && isPersonName(v))
      || (
        (node.firstName || node.first_name)
          ? `${node.firstName || node.first_name} ${node.lastName || node.last_name || ''}`.trim()
          : null
      )
    )
    : null;

  if (ownerName) {
    [node.email, node.userEmail, node.emailAddress].forEach((e) => {
      if (typeof e === 'string' && e.includes('@')) rememberEmailOwner(e, ownerName);
    });
  }

  // Learn this object's identities before masking any of its prose. Key order
  // in a JSON payload is not guaranteed to put `names` before `title`, and a
  // sentence masked before its own row's names were registered would keep the
  // real name.
  if (!NON_PERSON_PARENTS.has(parentKey) && !parentIsOrg) {
    for (const key of Object.keys(node)) {
      const v = node[key];
      if (typeof v === 'string' && NAME_FIELDS.has(key) && isPersonName(v)) {
        v.split(/\s*,\s*|\s+and\s+|\s*&\s*/).forEach((part) => {
          const p = part.trim();
          if (p && !/^\d+\s*(others?|more|people|members?)$/i.test(p)) learnIdentity(p);
        });
      }
    }
    if (ownerName) learnIdentity(ownerName);
  }

  for (const key of Object.keys(node)) {
    const value = node[key];

    if (typeof value === 'string') {
      if (ORG_NAME_FIELDS.has(key)) {
        out[key] = fakeOrgName();
        continue;
      }
      if (NAME_FIELDS.has(key)) {
        if (parentIsOrg && key === 'name') {
          out[key] = fakeOrgName();
        } else if (NON_PERSON_PARENTS.has(parentKey)) {
          out[key] = value; // a project/department/app name — leave it alone
        } else if (isPersonName(value)) {
          out[key] = maskNameList(value);
        } else {
          out[key] = value;
        }
        continue;
      }
      if (FIRST_NAME_FIELDS.has(key)) {
        out[key] = fakeFirstName(`${value} ${node.lastName || node.last_name || ''}`.trim());
        continue;
      }
      if (LAST_NAME_FIELDS.has(key)) {
        out[key] = fakeLastName(`${node.firstName || node.first_name || ''} ${value}`.trim());
        continue;
      }
      if (EMAIL_FIELDS.has(key) && value.includes('@')) {
        out[key] = fakeEmail(value, ownerName);
        continue;
      }
      // Every other string is run through prose masking. A whitelist of prose
      // fields is the wrong shape for this problem: the dashboard alone spans
      // dozens of screens with their own vocabulary (`lead`, `what`, `sub`,
      // `points`, …), and any field the list had not heard of would render a
      // real name. Prose masking only rewrites identities it already knows, so
      // applying it everywhere is safe — a string mentioning nobody comes back
      // unchanged — and it fails closed instead of open.
      out[key] = OPAQUE_FIELDS.has(key) ? value : maskText(value);
      continue;
    }

    out[key] = walk(value, key, depth + 1);
  }

  return out;
}

/* ── prose masking ─────────────────────────────────────────────────────── */
//
// AI narrative is the hard case: names are inside sentences, not fields, and
// the model refers to the same person as "Rajiv Sharma", "Rajiv", "Sharma" and
// "Rajiv's" within a paragraph. All four forms have to move together, or the
// text contradicts the roster shown beside it.

let ruleCache = { key: '', rules: [] };

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a replacer that echoes the case of whatever it matched, so "RAJIV" in
 * a heading becomes "MEERA" and "rajiv" mid-sentence becomes "meera", rather
 * than both collapsing to title case.
 */
function matchCase(replacement) {
  return (matched) => {
    if (matched === matched.toUpperCase() && matched !== matched.toLowerCase()) {
      return replacement.toUpperCase();
    }
    if (matched === matched.toLowerCase()) return replacement.toLowerCase();
    return replacement;
  };
}

/**
 * Register the real identities present in the current view so prose masking
 * knows what to look for. Callers pass whatever names/emails they already have
 * (roster responses, member lists); rules are rebuilt only when the set changes.
 */
// Every real identity seen so far, from any source. Prose rules are rebuilt
// from this, so a name learned from a structured field immediately becomes
// maskable inside sentences too.
const identityPool = new Set();

/**
 * Teach the prose masker about real identities. Additive: callers pass what
 * they have (a roster response, one alert row), and the rule set grows.
 */
export function setKnownIdentities(names) {
  (names || []).forEach((n) => {
    if (typeof n === 'string' && n.trim().length > 1) identityPool.add(n.trim());
  });

  const list = Array.from(identityPool);
  const key = list.join('|');
  if (key === ruleCache.key) return;

  const rules = [];
  list.forEach((real) => {
    const trimmed = real.trim();

    if (trimmed.includes('@')) {
      rules.push([new RegExp(escapeRe(trimmed), 'gi'), fakeEmail(trimmed)]);
      return;
    }

    const fake = fakeFullName(trimmed);
    const [realFirst, ...restReal] = trimmed.split(/\s+/);
    const realLast = restReal.join(' ');
    const [fakeFirst, fakeLast] = fake.split(' ');

    // Matching is case-insensitive: generated copy writes "rajiv" in a
    // sentence and "RAJIV" in a heading, and a case-sensitive rule would print
    // the real name in both. The replacement mirrors whatever case it matched,
    // so a heading stays a heading.
    const push = (pattern, replacement) => {
      rules.push([new RegExp(pattern, 'gi'), matchCase(replacement)]);
    };

    // Longest form first: "Rajiv Sharma's" must not be consumed by the "Rajiv"
    // rule before it is seen.
    push(`\\b${escapeRe(trimmed)}'s`, `${fake}'s`);
    push(`\\b${escapeRe(trimmed)}\\b`, fake);
    if (realFirst && realFirst.length > 2) {
      push(`\\b${escapeRe(realFirst)}'s\\b`, `${fakeFirst}'s`);
      push(`\\b${escapeRe(realFirst)}\\b`, fakeFirst);
    }
    // Surname alone maps to the invented surname alone — "Sharma" becomes
    // "Rao", not "Meera Rao" — so a paragraph that switches between "Rajiv" and
    // "Sharma" still reads as one person afterwards rather than two.
    if (realLast && realLast.length > 2) {
      push(`\\b${escapeRe(realLast)}'s\\b`, `${fakeLast}'s`);
      push(`\\b${escapeRe(realLast)}\\b`, fakeLast);
    }
  });

  ruleCache = { key, rules };
}

export function maskText(text) {
  if (!isDemoMode() || typeof text !== 'string' || !text) return text;
  let out = text;
  ruleCache.rules.forEach(([re, to]) => { out = out.replace(re, to); });
  return out;
}

/* ── streaming ─────────────────────────────────────────────────────────── */

/**
 * Masks SSE/chunked prose across frame boundaries.
 *
 * A streamed name can be split between frames — "Raj" arrives, then "iv spent
 * the morning…". Masking each frame independently would match neither half and
 * emit the real name. So the trailing partial token is withheld until the next
 * frame completes it, and `flush()` releases whatever is left when the stream
 * ends.
 */
export function createStreamMasker() {
  let held = '';

  return {
    push(chunk) {
      if (!isDemoMode()) return chunk;
      const buffer = held + chunk;

      // Release up to the last boundary; keep the unfinished tail.
      const cut = Math.max(
        buffer.lastIndexOf(' '),
        buffer.lastIndexOf('\n'),
        buffer.lastIndexOf('\t')
      );

      if (cut === -1) {
        // No boundary yet. Hold, unless the tail has grown past any plausible
        // name — at which point holding further would stall rendering.
        if (buffer.length > 120) {
          held = '';
          return maskText(buffer);
        }
        held = buffer;
        return '';
      }

      held = buffer.slice(cut + 1);
      return maskText(buffer.slice(0, cut + 1));
    },

    flush() {
      if (!isDemoMode() || !held) {
        const tail = held;
        held = '';
        return tail;
      }
      const tail = maskText(held);
      held = '';
      return tail;
    },
  };
}

export default {
  isDemoMode,
  enableDemoMode,
  disableDemoMode,
  maskObject,
  maskText,
  setKnownIdentities,
  createStreamMasker,
  fakeFullName,
  fakeEmail,
  fakeOrgName,
};
